/*
 * Geo location popup (sections/geo-location-popup.liquid).
 *
 * Offers visitors the other Billini store, based on their detected country:
 *   - AU store: visitors from the section's US-based countries (default
 *     US, MX, CA) are offered the US site.
 *   - US store (hostname contains us-billini.com): visitors from anywhere
 *     else are offered the Australian site.
 *
 * The country comes from Shopify's /browsing_context_suggestions.json and is
 * cached in localStorage ("detectedCountry"). Dismissing the popup sets
 * sessionStorage "geoPopupClosed", so it stays closed for the session.
 *
 * Readable source of the minified script first installed on the live themes;
 * behaviour is unchanged.
 */
class GeoLocationPopup extends HTMLElement {
	constructor() {
		super();
		this.isUSStore = window.location.hostname.includes("us-billini.com");

		const usBasedCountries = this.dataset.usBasedCountries || "US,MX,CA";
		this.northAmericanCountries = usBasedCountries
			.split(",")
			.map((country) => country.trim().toUpperCase());

		if (sessionStorage.getItem("geoPopupClosed") === "true") return;

		this.bindEvents();
		this.initializeGeolocation();
	}

	addClickEvent(selector, handler) {
		const element = this.querySelector(selector);
		if (element) {
			element.addEventListener("click", () => handler());
		} else {
			console.warn(`Element not found: ${selector}`);
		}
	}

	bindEvents() {
		this.addClickEvent(".geo-location-modal__overlay", () => this.close());
		this.addClickEvent(".geo-location-modal__close-button", () => this.close());
		this.addClickEvent(".geo-location-modal__button--secondary", () =>
			this.close()
		);
	}

	async initializeGeolocation() {
		try {
			let country;
			const cachedCountry = localStorage.getItem("detectedCountry");

			if (cachedCountry) {
				country = JSON.parse(cachedCountry);
			} else {
				const data = await this.fetchGeolocationData();
				if (data?.detected_values?.country) {
					country = data.detected_values.country;
					localStorage.setItem("detectedCountry", JSON.stringify(country));
				}
			}

			if (!country) return;

			this.country_code = country.handle.toUpperCase();
			if (!this.shouldShowPopup()) return;

			this.updateLocation(country);
			this.checkAndShowPopup();
		} catch (error) {
			console.error("Error initializing geolocation:", error);
		}
	}

	async fetchGeolocationData() {
		const response = await fetch("/browsing_context_suggestions.json");
		return response.json();
	}

	updateLocation(country) {
		this.country = country.name;
		this.country_code = country.handle.toUpperCase();

		const redirectCountry = this.isUSStore ? "Australian" : "USA";
		const redirectUrl = this.dataset.redirectUrl;

		this.querySelectorAll(".geolocation__redirect-country").forEach(
			(element) => {
				element.textContent = redirectCountry;
			}
		);

		const detectedCountry = this.querySelector(".geolocation__detected-country");
		if (detectedCountry) detectedCountry.textContent = this.country;

		const storeCountry = this.querySelector(".geolocation__store-country");
		if (storeCountry) {
			storeCountry.textContent = this.isUSStore ? "USA" : "Australian";
		}

		const redirectLink = this.querySelector(".geolocation__redirect-country-url");
		if (redirectLink) redirectLink.href = redirectUrl;
	}

	shouldShowPopup() {
		if (sessionStorage.getItem("geoPopupClosed") === "true") return false;

		const isNorthAmerican = this.northAmericanCountries.includes(
			this.country_code
		);
		return this.isUSStore ? !isNorthAmerican : isNorthAmerican;
	}

	checkAndShowPopup() {
		if (!this.shouldShowPopup()) return;

		const delaySeconds = parseInt(this.dataset.popupDelay || "3", 10);
		setTimeout(() => this.open(), delaySeconds * 1000);
	}

	open() {
		this.classList.remove("hidden");
		document.body.classList.add("overflow-hidden");
	}

	close() {
		this.classList.add("hidden");
		document.body.classList.remove("overflow-hidden");
		sessionStorage.setItem("geoPopupClosed", "true");
	}
}

customElements.define("geo-location-popup", GeoLocationPopup);
